"use client";

import Link from "next/link";
import { ArrowRight, BookOpen } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  LiveDot,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  StepsCard,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { FlowDriftMock, FlowTradesMock } from "@/components/marketing/dev-landing/mocks";
import { C, CodeWindow, K, Line, N, P, S } from "@/components/marketing/dev-landing/code-window";
import { EndpointTable } from "@/components/marketing/dev-landing/endpoint-table";

const q = (v: string) => `"${v}"`;

export default function OptionsFlowsApiLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <LiveDot />
          <span className="text-sm text-[#d4d5d5]">Real-time options flow &middot; straight off the tape</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          The real-time <span style={metalText}>options flow</span> API
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          Net call and put premium bars plus significant prints - sweeps, blocks and splits - delivered
          as compact JSON built for charts, bots and alerts.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="flows_api_lp_get_key_clicked" />
          <SecondaryCta href="/docs">
            <BookOpen className="h-4 w-4" />
            Read the docs
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Part of the Quant plan.</p>
      </div>

      {/* Hero demo: drift chart + prints tape */}
      <div className="relative mx-auto mt-12 max-w-5xl md:mt-16">
        <div
          className="pointer-events-none absolute -inset-12 rounded-full opacity-20"
          style={{
            background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative grid gap-5 md:grid-cols-2">
          <FlowDriftMock />
          <FlowTradesMock />
        </div>
      </div>

      <StatsRow
        stats={[
          { value: "1m", label: "bar resolution" },
          { value: "Live", label: "intraday updates" },
          { value: "3", label: "numbers per bar" },
          { value: "Any", label: "US optionable ticker" },
        ]}
      />

      {/* Features */}
      <div className="mt-24 sm:mt-28">
        <SectionHeading
          title={
            <>
              Follow the premium, <span style={metalText}>not the noise</span>
            </>
          }
          sub="Flow that answers one question fast: is real money leaning into calls or puts, right now - and who just placed the big bet?"
        />
      </div>

      <FeatureSplit
        eyebrow="Premium drift"
        title={
          <>
            One array per session, <span style={metalText}>trivially chartable</span>
          </>
        }
        lead="Aggressor-signed call and put premium bucketed per minute. Three numbers per bar - timestamp, net call premium, net put premium - small enough to poll from anything."
        points={[
          { term: "[ts, call, put]", text: "positional arrays instead of verbose objects" },
          { term: "resolution=1m", text: "down to the minute for scalps, coarser for swings" },
          { term: "day=YYYY-MM-DD", text: "replay any past session, bar by bar" },
        ]}
        visual={
          <CodeWindow title="/api/v1/flows?symbol=NVDA&resolution=1m" method="GET" status="200 - 41ms">
            <Line>
              <P>{"{"}</P>
            </Line>
            <Line indent={1}>
              <K>{q("symbol")}</K>
              <P>: </P>
              <S>{q("NVDA")}</S>
              <P>, </P>
              <K>{q("resolution")}</K>
              <P>: </P>
              <S>{q("1m")}</S>
              <P>, </P>
              <K>{q("count")}</K>
              <P>: </P>
              <N>391</N>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("bars")}</K>
              <P>: [ </P>
              <C>{"// [ts, netCallPremium, netPutPremium]"}</C>
            </Line>
            <Line indent={2}>
              <P>[</P>
              <N>1754404260</N>
              <P>, </P>
              <N>1420000</N>
              <P>, </P>
              <N>-880000</N>
              <P>],</P>
            </Line>
            <Line indent={2}>
              <P>[</P>
              <N>1754404320</N>
              <P>, </P>
              <N>2140000</N>
              <P>, </P>
              <N>-460000</N>
              <P>]</P>
            </Line>
            <Line indent={1}>
              <P>],</P>
            </Line>
            <Line indent={1}>
              <K>{q("asOf")}</K>
              <P>: </P>
              <S>{q("2026-08-05T14:32:05Z")}</S>
            </Line>
            <Line>
              <P>{"}"}</P>
            </Line>
          </CodeWindow>
        }
      />

      <FeatureSplit
        flip
        eyebrow="Significant prints"
        title={
          <>
            The trades that <span style={metalText}>move positioning</span>
          </>
        }
        lead="Sweeps, blocks and splits above the noise floor - each with side, strike, expiration and premium, so your code can explain a move instead of just noticing it."
        points={[
          { term: "type", text: "SWEEP, BLOCK or SPLIT - classified off the tape" },
          { term: "side", text: "at the ask, at the bid, or midpoint" },
          { term: "premium", text: "dollar size, so your alerts can set a floor" },
        ]}
        visual={<FlowTradesMock />}
      />

      {/* Endpoints */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="Two endpoints, the whole story"
          sub="Premium drift for the trend, prints for the trigger. Both under the same key."
        />
        <EndpointTable
          endpoints={[
            { method: "GET", path: "/v1/flows", description: "Net call/put premium bars, live or by day", tag: "Quant" },
            { method: "GET", path: "/v1/flows/trades", description: "Significant prints: sweeps, blocks, splits", tag: "Quant" },
            { method: "GET", path: "/v1/gex", description: "Pair flows with real-time dealer positioning" },
            { method: "GET", path: "/v1/expirations", description: "Available expirations per symbol" },
          ]}
        />
        <p className="mt-5 text-center text-sm text-white/50">
          <Link href="/docs" className="inline-flex items-center gap-1.5 text-[#7fe7c8] underline-offset-4 hover:underline">
            Full flows API reference
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </p>
      </div>

      {/* How it works */}
      <StepsCard
        title="Plugged into the tape in three steps"
        steps={[
          {
            title: "Get on Quant",
            description: "The options flows API ships with the Quant plan, alongside the full API catalog.",
          },
          {
            title: "Generate your key",
            description: (
              <>
                Mint a key in{" "}
                <Link href="/dashboard/api" className="text-[#7fe7c8] underline-offset-4 hover:underline">
                  Dashboard &rarr; API
                </Link>
                . Bearer or X-API-Key, your call.
              </>
            ),
          },
          {
            title: "Poll and plot",
            description: "GET /v1/flows once a minute and you have a live premium drift chart. That's it.",
          },
        ]}
      />

      <div className="mt-20 sm:mt-24">
        <TextFeatureList
          items={[
            {
              title: "Truly real-time",
              description:
                "Bars update live intraday - the same feed that powers QuantWheel's flow dashboards and the TradingView overlay, not an end-of-day file.",
            },
            {
              title: "Compact by design",
              description:
                "Payloads stay small enough to poll every minute from bots, dashboards and serverless functions without blowing budgets.",
            },
          ]}
        />
      </div>

      <CrossSellBand
        badge="Prefer prompts over polling?"
        title="Ask your AI what's sweeping right now"
        description="The same flow feed ships as MCP tools - connect QuantWheel to Claude or Cursor and ask about drift and prints in plain English."
        href="/options-flows-mcp"
        linkLabel="Explore the Flows MCP"
      />

      <CrossSellBand
        className="mt-5"
        badge="Want the full catalog?"
        title="GEX, screeners and analytics under the same key"
        description="Flows is one surface of the QuantWheel API - the overview page tours everything else your key unlocks."
        href="/api"
        linkLabel="See the full API"
      />

      <FinalCta
        title="Trade with the tape, not behind it"
        sub="One key unlocks real-time options flow - premium drift and significant prints - for any US optionable ticker."
        keyHref={keyHref}
        eventName="flows_api_lp_get_key_clicked"
        note="Options flows are part of the Quant plan."
      />
    </DevLandingShell>
  );
}
